import axios from 'axios';
import { authService } from '../services/authService';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_END_POINT_API || 'http://localhost:3000/',
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to check if token is expired
const isTokenExpired = () => {
  const expiresIn = localStorage.getItem('tokenExpires');
  if (!expiresIn) return true;

  // Convert expiration time to milliseconds and add buffer time (e.g., 10 seconds)
  const expirationTime = Number(expiresIn) * 1000;
  const currentTime = Date.now();
  const bufferTime = 10 * 1000; // 10 seconds buffer

  return currentTime + bufferTime >= expirationTime;
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and token is expired
    if (error.response?.status === 401 && !originalRequest._retry && isTokenExpired()) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        processQueue(error);
        return Promise.reject(error);
      }

      try {
        const response = await authService.refreshToken(refreshToken);
        
        // Update tokens and expiration time
        localStorage.setItem('accessToken', response.access_token);
        localStorage.setItem('refreshToken', response.refresh_token);
        localStorage.setItem('tokenExpires', response.expires_in.toString());
        
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${response.access_token}`;
        processQueue(null, response.access_token);
        
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpires');
        localStorage.removeItem('userRoles');
        localStorage.removeItem('isAuthenticated');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance; 